import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cookies (httpOnly admin session) are sent automatically with every request.
export const api = axios.create({ baseURL: API, withCredentials: true });

// On 401 to an admin route, reload /admin so <AdminPage/> re-checks session.
// Exclude:
//   - /admin/me   → 401 by design when signed-out (reload would loop)
//   - /admin/login → 401 means bad credentials, not session-expiry (reload would
//     wipe the login form + drop the error toast before the user can read it)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const url = err.config?.url || "";
      const isAdminRoute = url.includes("/admin/");
      const isSessionCheck = url.endsWith("/admin/me") || url.includes("/admin/me?");
      const isLogin = url.endsWith("/admin/login") || url.includes("/admin/login?");
      if (isAdminRoute && !isSessionCheck && !isLogin && typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
        window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

export const ESD_DISCOUNT = 0.30;
export const CSP_DISCOUNT = 0.0125;

// HPE hardware — indicative "up to" partner discount shown against list price.
// Actual quote from sales may vary based on volume, promo, and margin.
export const HPE_MAX_DISCOUNT = 0.35;
export const hpeDiscounted = (listPrice) => Math.round(Number(listPrice || 0) * (1 - HPE_MAX_DISCOUNT));

/**
 * Build a SEO-friendly slug from a product for use in URLs.
 * Mirror of `product_slug()` in backend/server.py.
 * Format: `<slug(name)>-<lowercased_sku>` (SKU tail guarantees uniqueness).
 * Returns product.id as fallback for legacy links.
 */
export function productSlug(product) {
  if (!product) return "";
  const name = String(product.name || "").trim().toLowerCase();
  const sku = String(product.sku || "").trim().toLowerCase();
  const base = name.replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const skuPart = sku.replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  if (!base && !skuPart) return (product.id || "").slice(0, 140);
  if (!skuPart) return base.slice(0, 140);
  const maxName = 140 - skuPart.length - 1;
  if (maxName < 8) return skuPart.slice(0, 140);
  const trimmedBase = base.slice(0, maxName).replace(/-+$/g, "");
  return trimmedBase ? `${trimmedBase}-${skuPart}` : skuPart;
}
export const productUrl = (product) => {
  // Chaos family pseudo-cards route to the family landing page under
  // `/chaos/<slug>` — see ProductsPage's chaos-family injection (18 Sep
  // 2026). Real product URLs continue to resolve to `/products/<slug>`.
  if (product && product._family_slug) return `/chaos/${product._family_slug}`;
  return `/products/${productSlug(product) || product?.id || ""}`;
};

/**
 * Given a product image URL, return the `srcSet` attribute value that
 * bundles the primary + 2× retina WebP variants uploaded by the admin
 * pipeline.
 *
 * Rules:
 *   * Only fires for our own uploaded WebP files (path starts with
 *     `/api/uploads/products/` AND ends with `.webp`). External CDN URLs
 *     (icons8, ctfassets, HPE dam) are returned as an empty srcset so the
 *     browser falls back to the plain `src` attribute.
 *   * Assumes the `@2x.webp` sibling exists — the upload endpoint writes
 *     both in one atomic call.
 *
 * Usage:
 *   <img src={image} srcSet={retinaSrcSet(image)} ... />
 */
export const retinaSrcSet = (image) => {
  if (!image || typeof image !== "string") return "";
  const trimmed = image.trim();
  // Only self-hosted uploaded webp files have a paired @2x variant.
  if (!trimmed.endsWith(".webp")) return "";
  if (!trimmed.includes("/api/uploads/products/")) return "";
  const retina = trimmed.replace(/\.webp$/, "@2x.webp");
  return `${trimmed} 1x, ${retina} 2x`;
};

// Legacy: fixed-percent helper (used only for products missing per-product overrides)
export const priceFor = (list, type) => {
  const d = type === "ESD" ? ESD_DISCOUNT : CSP_DISCOUNT;
  return Math.round(list * (1 - d));
};

// Per-product discount percent (respects product overrides)
export const productDiscountPct = (product, type) => {
  if (!product) return type === "ESD" ? ESD_DISCOUNT * 100 : CSP_DISCOUNT * 100;
  if (type === "ESD") return product.esd_discount_pct ?? ESD_DISCOUNT * 100;
  return product.csp_discount_pct ?? CSP_DISCOUNT * 100;
};

// Compute the price for a product + type, respecting per-product discount override
export const priceForProduct = (product, type) => {
  const pct = productDiscountPct(product, type) / 100;
  return Math.round((product?.list_price || 0) * (1 - pct));
};

export const isTypeEnabled = (product, type) => {
  if (!product) return true;
  if (type === "ESD") return product.esd_enabled !== false;
  if (type === "CSP") return product.csp_enabled !== false;
  return true;
};

export const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

/**
 * Universal "best discount" for a product — used to render the "Up to X% OFF"
 * ribbon shown on every product card, product detail page, and search result.
 * Returns an integer percent (0-100).
 *
 * Precedence: explicit rules first (HPE, Open Value, M365, ESD), then a
 * brand/category-based fallback so every product carries a marketing badge.
 */
export const maxDiscountPct = (product) => {
  if (!product) return 0;
  // ---- Admin explicit override (highest precedence) ----
  // When an admin has set a non-zero `discount_pct` from the Quick-Edit
  // panel, honour it VERBATIM — no category cascade, no vendor fallback.
  // A `0` deliberately falls through so the marketing badge still shows
  // for products the admin hasn't touched.
  const adminPct = Number(product.discount_pct);
  if (Number.isFinite(adminPct) && adminPct > 0) {
    return Math.round(adminPct);
  }
  // Tally MRP is set by Tally Solutions — we honour the exact
  // `discount_pct` seeded on the SKU (max 3%). Never fall through to
  // a generic 30% ESD figure or the ribbon will lie.
  if (product.product_type === "tally") {
    return Math.round(Number(product.discount_pct) || 0);
  }
  // Admin-managed MS subscriptions (NCE-style) — always show the exact
  // discount_pct set by the admin (0 hides the ribbon).
  if (product.product_type === "ms_subscription") {
    return Math.round(Number(product.discount_pct) || 0);
  }
  // Quick Heal / Seqrite retail SKUs — 10% partner MRP discount. Matches
  // the `partnerDiscountPct` constant used inside BrandedSecurityPricing
  // (single source of truth would be nicer but this is a one-line ribbon).
  // Contact-for-price SKUs (list_price=0, no published MRP) intentionally
  // return 0 so the discount ribbon does NOT render on the hero image —
  // advertising "10% OFF" on a Custom-Quote SKU is misleading.
  if (["quickheal", "seqrite"].includes(product.product_type)) {
    if (product.contact_for_price === true || Number(product.list_price || 0) === 0) return 0;
    return 10;
  }
  // Chaos (V-Ray / Corona / Enscape / Evolvelab / Anima / Credits / Education)
  // are MRP + Get Quote only — no partner discount tier is advertised anywhere.
  // Return 0 explicitly so the "Up to X% OFF" ribbon and green "UP TO 15% OFF"
  // pill are both suppressed on cards, PDPs and family pages.
  if (product.product_type === "chaos") return 0;
  // Microsoft Hot Sellers (mirrored from /microsoft page) — always advertise the
  // "up to 45%" partner rate everywhere the SKU appears. Detected by the `ms-`
  // id prefix used by _mirror_into_products (ms_hot_sellers.py).
  if (product.id && String(product.id).startsWith("ms-")) return 45;
  const cat = product.category || "";
  // HPE hardware
  if (["Rack Servers", "Tower Servers"].includes(cat)) {
    return Math.round(HPE_MAX_DISCOUNT * 100);
  }
  // Open Value
  if (["Open Value", "Open Value Subscription"].includes(cat)) {
    const erp = Number(product.list_price) || 0;
    // Same figure the price panel subtracts, so the ribbon never over-claims.
    return erp > 0 ? Math.round((openValueDiscount(product) / erp) * 100) : 5;
  }
  // M365 monthly/yearly — compute % from list vs yearly price
  if (product.ms_price_yearly && product.price_yearly) {
    const pct = Math.round(((product.ms_price_yearly - product.price_yearly) / product.ms_price_yearly) * 100);
    if (pct > 0) return pct;
  }
  // ESD-enabled perpetual licenses (Microsoft ESD is 30%)
  if (product.esd_enabled !== false && (product.esd_discount_pct ?? ESD_DISCOUNT * 100) > 0) {
    return Math.round(product.esd_discount_pct ?? ESD_DISCOUNT * 100);
  }
  // ---- Brand/category fallback (never leave a card badge-less) ----
  // Adobe suites — user has agreed to advertise 25% max discount
  if (["Adobe", "Photo Editing", "Video Editing", "PDF & Productivity"].includes(cat)) return 25;
  // Autodesk
  if (["Autodesk", "CAD & Technical"].includes(cat)) return 20;
  // Zoho — user has decided to advertise 15% (matches Zoho detail page banner)
  if (cat === "Zoho") return 15;
  // Corel
  if (cat === "Graphics & Design") return 25;
  // SketchUp
  if (["Core Plans", "Pro Plans", "Education Plans"].includes(cat)) return 20;
  // Anything else — a reasonable partner-discount hint
  return 15;
};

// Open Value / Open Value Subscription — discount = min(₹5,000, 5% × ERP, ERP − DTP)
export const OV_DISCOUNT_CAP = 5000;
export const OV_DISCOUNT_PCT = 0.05;
export const openValueDiscount = (product) => {
  if (!product) return 0;
  const erp = Number(product.list_price) || 0;
  if (erp <= 0) return 0;
  // Public API ships the server-computed discount; `dtp` is only present in admin data.
  if (typeof product.ov_discount === "number") return Math.max(0, product.ov_discount);
  const dtp = Number(product.dtp);
  const caps = [OV_DISCOUNT_CAP, OV_DISCOUNT_PCT * erp];
  if (Number.isFinite(dtp) && dtp > 0) {
    caps.push(Math.max(0, erp - dtp));
  }
  return Math.max(0, Math.min(...caps));
};
export const openValueFinalPrice = (product) =>
  Math.max(0, (Number(product?.list_price) || 0) - openValueDiscount(product));
export const isOpenValueType = (product) =>
  product?.product_type === "open_value" || product?.product_type === "open_value_subscription";

// Backwards-compat helper: cookies are now sent automatically.
// Retained as an empty header so any lingering callers don't crash.
export const adminAuthHeader = () => ({});

/**
 * Mirrored MS Hot Sellers use an `ms-...` id prefix (see backend
 * `_mirror_into_products`). Used to render gold-border/best-seller styling
 * inside the merged /products catalog.
 */
export const isMsHotSeller = (product) =>
  !!(product && product.id && String(product.id).startsWith("ms-"));
